import React, { useState, useEffect } from "react";
import { Card, Col, Row } from "reactstrap";
import { useVsiAppContext } from "../../../stores/VsiApp";
import { useDataWithRoute, useTitleFromComponent } from "../../../utils/hooks";
import { forecastWithRate, formatStockChartConfig } from "../../../utils/Helpers";
import { VsiBubble, VsiH4, VsiMedLabel, VsiXLText } from "../../../styled/App.styled";
import Stockchart from "../../../components/Stockchart";
import { enterpriseTrendColors } from "../../../utils/chartUtils";
import BarChart from "../../../components/BarChart";
import FlexChart from "../../../components/FlexChart";
import { vsiGreen, vsiRed } from "../../../utils/themeStyles";
import { growthFormatter, growthTrendFormatter } from "../../../utils/gridFormatters";

const stockConfig = {
    colors: enterpriseTrendColors,
    yAxis: {
        title: {
            text: "Cost $"
        }
    }
};

export default function KubernetesBudgetingReport({ title, context }) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [{ recordsets }] = useDataWithRoute(`/api/v6/report/kubernetes_budget_proc?client_id=${client.clientId}&period_id=${periodId}`);
    const [avgContainerCostData, highestContainerCostData, budgetTrendData] = recordsets;

    const [avgCostTrend, setAvgCostTrend] = useState([]);
    const [highCostBar, setHighCostBar] = useState({});
    const [highCostCategories, setHighCostCategories] = useState([]);
    const [budgetTrend, setBudgetTrend] = useState([]);

    const [budgetSummary, setBudgetSummary] = useState({});

    useEffect(() => {
        const allContainers = (avgContainerCostData || []).reduce((acc, datum) => {
            if (!acc.hasOwnProperty(datum.container_name)) {
                acc[datum.container_name] = true;
            }
            return acc;
        }, {});

        const allFields = Object.keys(allContainers).map(cont => ({
            name: cont,
            field: "average_cost_per_container",
            dataLabels: { format: "${point.y:f}" },
            tooltip: { pointFormat: "{series.name}: <b>${point.y:f}</b>" }
        }));
        const stockConfig = formatStockChartConfig((avgContainerCostData || []), allFields, "container_name");

        setAvgCostTrend(stockConfig.series);
    }, [avgContainerCostData]);

    useEffect(() => {
        const highCostSeries = (highestContainerCostData || []).map(cont => cont.total_cost_per_container);

        setHighCostCategories((highestContainerCostData || []).map(({ container_name }) => container_name));
        setHighCostBar({
            name: "Cost $",
            data: highCostSeries
        });
    }, [highestContainerCostData]);

    useEffect(() => {
        const dataToUse = (budgetTrendData ?? []).map(datum => ({
            ...datum,
            // TODO: get budget from tagging when k8 tagging is available
            budget: datum.monthly_total_cost * 1.25
        }));
        const budgetData = forecastWithRate(dataToUse || [], ["monthly_total_cost"], 7, (budgetTrendData?.[0]?.growth_rate ?? 0) / 100);

        const allSeriesData = formatStockChartConfig(budgetData || [], [
            {
                type: "areaspline",
                name: "Accumulated Cost",
                field: "monthly_total_cost",
                zoneAxis: "x",
                zones: [{
                    value: Date.parse(
                        dataToUse?.[dataToUse.length - 1]?.collection_date?.split?.("T")?.[0]
                    )
                },
                    {
                        dashStyle: "Dash"
                    }],
                disableMarker: true,
                colorFilter: {
                    name: "forecast"
                }
            },
            {
                type: "spline",
                name: "Monthly Budget",
                field: "budget",
                zoneAxis: "x",
                zones: [{
                    value: Date.parse(
                        dataToUse?.[dataToUse.length - 1]?.collection_date?.split?.("T")?.[0]
                    )
                },
                    {
                        dashStyle: "Dash"
                    }],
                disableMarker: true
            }
        ]);

        setBudgetTrend(allSeriesData.series);
        setBudgetSummary(dataToUse?.[0] ?? {});

    }, [budgetTrendData]);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Kubernetes Budgeting Report</VsiH4>
        <Row>
        <Col xs={12}>
        <VsiBubble noAutobot>
        <div className={"d-flex flex-row align-items-start ms-4"}>
        <div className={"d-flex flex-column align-items-start me-4"}>
        <VsiMedLabel>Monthly Cost</VsiMedLabel>
        <VsiXLText>
        ${budgetSummary?.monthly_total_cost?.toLocaleString?.("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) ?? "-"}
        </VsiXLText>
        </div>
        <div className={"d-flex flex-column align-items-start ms-4"}>
        <VsiMedLabel>Rate of Growth</VsiMedLabel>
        <VsiXLText>
        {budgetSummary?.growth_rate ? (
            <>
            {growthTrendFormatter({
                value: budgetSummary?.growth_rate
            })}
            </>
        ) : "-"}
        </VsiXLText>
        </div>
        </div>
        <FlexChart series={budgetTrend}
        height={400}
        title={"Budget Forecast"}
        config={{
            colors: [vsiGreen, vsiRed],
                yAxis: {
                    title: {
                        text: "Cost $"
                    }
                }
        }} />
        </VsiBubble>
        </Col>
        <Col xs={7}>
        <VsiBubble noAutobot>
        <Stockchart series={avgCostTrend}
        height={500}
        formatLabels={"${value:,f}"}
        formatData={"${point.y:,2f}"}
        title={"Average Cost per Container"}
        config={stockConfig} />
        </VsiBubble>
        </Col>
        <Col xs={5}>
        <VsiBubble noAutobot>
        <BarChart series={highCostBar}
        height={500}
        categories={highCostCategories}
        title={"Highest Cost Containers"} />
        </VsiBubble>

        </Col>
        </Row>
        </Card>
    );
}
